import type { ReactNode, SVGProps } from 'react';

/**
 * The app's one icon set.
 *
 * Every icon is drawn on the same 24-unit grid with the same stroke weight,
 * round caps and joins, and `currentColor` — so an icon is ink wherever the
 * text around it is ink, and nothing renders as a platform emoji (the old ⚙
 * tab came out as a colour gear on iOS). People are drawn the way the app
 * mark is: a circle head and single-stroke limbs.
 */
export type IconName =
  | 'progress'
  | 'session'
  | 'coach'
  | 'exercises'
  | 'plan'
  | 'settings'
  | 'close'
  | 'check'
  | 'alert'
  | 'back'
  | 'send'
  | 'plus'
  | 'minus'
  | 'star'
  | 'flipCamera'
  | 'play'
  | 'pause'
  | 'prevMark'
  | 'nextMark'
  | 'headForward'
  | 'slouch'
  | 'recline'
  | 'unknown';

const PATHS: Record<IconName, ReactNode> = {
  // A rising trend on a baseline.
  progress: (
    <>
      <path d="M4 19h16" />
      <path d="M5 15l4.5-4.5 3.5 3L19 7.5" />
      <path d="M15 7.5h4v4" />
    </>
  ),
  // The phone camera that runs the session.
  session: (
    <>
      <rect x="3" y="6.5" width="18" height="13" rx="3" />
      <path d="M8.5 6.5l1.2-2h4.6l1.2 2" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  // A speech bubble: Darwin answers questions.
  coach: (
    <>
      <path d="M5 5.5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-8l-4.5 3.5V17.5H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2z" />
      <path d="M8 10.5h8M8 13.5h5" />
    </>
  ),
  // The figure, arms raised in a stretch.
  exercises: (
    <>
      <circle cx="12" cy="5" r="2.2" />
      <path d="M12 8.5v6.5M12 15l-3.5 5.5M12 15l3.5 5.5" />
      <path d="M12 10L7 5.5M12 10l5-4.5" />
    </>
  ),
  // A calendar page with a tick: the week's plan.
  plan: (
    <>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2.5" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
      <path d="M9 15l2 2 4-4" />
    </>
  ),
  // Three sliders.
  settings: (
    <>
      <path d="M4 7h9M17 7h3M4 12h3M11 12h9M4 17h11M19 17h1" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="17" r="2" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  alert: (
    <>
      <path d="M12 6.5v7" />
      <path d="M12 17.5h.01" />
    </>
  ),
  back: <path d="M15 5l-7 7 7 7" />,
  send: (
    <>
      <path d="M12 19V5" />
      <path d="M6 11l6-6 6 6" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  star: (
    <path d="M12 4l2.4 5 5.4.6-4 3.7 1.1 5.3L12 16l-4.9 2.6 1.1-5.3-4-3.7 5.4-.6z" />
  ),
  // Camera with a turn-around arrow.
  flipCamera: (
    <>
      <rect x="3" y="6.5" width="18" height="13" rx="3" />
      <path d="M8.5 6.5l1.2-2h4.6l1.2 2" />
      <path d="M15.5 12a3.5 3.5 0 0 0-6.2-2.2" />
      <path d="M9 8.5v1.8h1.8" />
      <path d="M8.5 14a3.5 3.5 0 0 0 6.2 2.2" />
      <path d="M15 17.5v-1.8h-1.8" />
    </>
  ),
  // Replay transport. Previous/next jump between slips, so they carry a bar.
  play: <path d="M8 5.5v13l10.5-6.5z" />,
  pause: <path d="M9 5.5v13M15 5.5v13" />,
  prevMark: <path d="M6.5 6v12M17.5 6l-7 6 7 6" />,
  nextMark: <path d="M17.5 6v12M6.5 6l7 6-7 6" />,
  // Posture states. Head ahead of the shoulders; trunk folding down; leaning back.
  headForward: (
    <>
      <circle cx="15" cy="7" r="2.6" />
      <path d="M8 20v-8.5l4.8-2.6" />
      <path d="M4 7h4" />
    </>
  ),
  slouch: (
    <>
      <circle cx="16" cy="9" r="2.6" />
      <path d="M8 20c0-5 1.5-8 5.5-9.5" />
      <path d="M18 15v5M16 18l2 2 2-2" />
    </>
  ),
  recline: (
    <>
      <circle cx="7.5" cy="6" r="2.6" />
      <path d="M15 20L9.5 9" />
      <path d="M15 20h5" />
    </>
  ),
  unknown: (
    <>
      <path d="M9.5 9a2.5 2.5 0 1 1 3.6 2.3c-.7.3-1.1 1-1.1 1.7v.8" />
      <path d="M12 17.5h.01" />
    </>
  ),
};

export function Icon({
  name,
  size = 22,
  strokeWidth = 1.8,
  ...rest
}: { name: IconName; size?: number; strokeWidth?: number } & Omit<
  SVGProps<SVGSVGElement>,
  'name'
>): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
